import { useState, useEffect, useCallback } from 'react'

const STORAGE_KEY = 'ai4solar_blog_upvotes'
const VOTED_KEY = 'ai4solar_blog_voted'

export function useBlogUpvotes() {
  // Store upvote counts: { postId: count }
  const [upvotes, setUpvotes] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  // Store which posts user has voted on: [postId1, postId2, ...]
  const [votedPosts, setVotedPosts] = useState(() => {
    try {
      const stored = localStorage.getItem(VOTED_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })

  // Persist upvotes to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(upvotes))
  }, [upvotes])

  // Persist voted posts to localStorage
  useEffect(() => {
    localStorage.setItem(VOTED_KEY, JSON.stringify(votedPosts))
  }, [votedPosts])

  // Check if user has already upvoted a post
  const hasUpvoted = useCallback((postId) => {
    return votedPosts.includes(postId)
  }, [votedPosts])

  // Handle upvote action (toggle behavior)
  const handleUpvote = useCallback((postId) => {
    if (hasUpvoted(postId)) {
      // Remove upvote
      setUpvotes(prev => ({
        ...prev,
        [postId]: Math.max((prev[postId] || 1) - 1, 0)
      }))
      setVotedPosts(prev => prev.filter(id => id !== postId))
    } else {
      // Add upvote
      setUpvotes(prev => ({
        ...prev,
        [postId]: (prev[postId] || 0) + 1
      }))
      setVotedPosts(prev => [...prev, postId])
    }
  }, [hasUpvoted])

  // Get upvote count for a specific post
  const getUpvoteCount = useCallback((postId, initialCount = 0) => {
    return upvotes[postId] !== undefined ? upvotes[postId] : initialCount
  }, [upvotes])

  return {
    upvotes,
    handleUpvote,
    hasUpvoted,
    getUpvoteCount
  }
}
